import { useEffect, useMemo, useState } from 'react';
import { StanLoader, StanModal, StanProgressBar } from '@/components';
import { StanCheckbox } from '@/components/core-components/StanCheckbox/StanCheckbox';
import { useBulkCustomerActions } from '../../hooks/useBulkCustomerActions';
import { formatNumber, stanNotify } from '@/lib';

interface BulkDeleteCustomersModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalCustomers: number;
    filterArray: string | undefined;
    onDeleteSuccess?: () => void;
}

export function BulkDeleteCustomersModal({
    isOpen,
    onClose,
    totalCustomers,
    filterArray,
    onDeleteSuccess,
}: BulkDeleteCustomersModalProps) {
    const [confirmed, setConfirmed] = useState(false);

    const { batchState, isLoading, deleteCustomers, resetState } = useBulkCustomerActions({
        filterArray,
        totalCustomers,
        onSuccess: result => {
            setConfirmed(false);

            // Show appropriate notification based on deletion result (matching Vue behavior)
            if (result) {
                const { totalDeleted = 0, totalCount = 0 } = result;

                if (totalCount === totalDeleted) {
                    // All customers deleted successfully
                    stanNotify({
                        type: 'success',
                        title: 'Success',
                        text: 'The customers have been deleted',
                        duration: 10000,
                    });
                } else {
                    // Partial deletion - some customers couldn't be deleted
                    stanNotify({
                        type: 'warning',
                        title: `${totalDeleted} of ${totalCount} customers deleted`,
                        text: 'Customers with a purchase history must be deleted manually.',
                        duration: 10000,
                    });
                }
            }

            onDeleteSuccess?.();
            onClose();
        },
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setConfirmed(false);
            resetState();
        }
    }, [isOpen, resetState]);

    const handleClose = () => {
        if (!isLoading) {
            setConfirmed(false);
            resetState();
            onClose();
        }
    };

    const handleSubmit = () => {
        if (!confirmed) return;
        deleteCustomers();
    };

    const modalTitle = useMemo(() => {
        if (isLoading) return 'Deleting...';
        return 'Are you sure?';
    }, [isLoading]);

    const customerText = totalCustomers === 1 ? 'customer' : 'customers';

    return (
        <StanModal
            id='bulk-delete-customers-modal'
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            primaryActionLabel={isLoading ? undefined : 'Delete Customers'}
            primaryActionColor='destructive'
            secondaryActionLabel={isLoading ? undefined : 'Cancel'}
            primaryActionDisabled={!confirmed}
            primaryActionLoading={isLoading}
            onPrimaryAction={handleSubmit}
            onSecondaryAction={handleClose}
            size='md'
        >
            <div className='flex flex-col gap-4'>
                {isLoading ? (
                    <div className='flex flex-col items-center gap-6 py-8'>
                        <StanLoader />
                        <StanProgressBar
                            min={0}
                            max={batchState.totalCount}
                            value={batchState.processedCount}
                            progressColor='var(--color-negative-main)'
                            className='w-full'
                        />
                        <p className='text-gray text-label-xs-regular text-center'>
                            {batchState.processedCount} / {batchState.totalCount} customers deleted
                        </p>
                    </div>
                ) : (
                    <div className='flex flex-col gap-6'>
                        <div className='bg-negative-subtle flex flex-col items-center justify-center rounded-2xl py-10'>
                            <span className='text-negative text-6xl font-bold'>
                                {formatNumber(totalCustomers, { decimals: 0 })}
                            </span>
                            <span className='text-negative text-label-sm-regular mt-8'>
                                {customerText} will be deleted
                            </span>
                        </div>

                        <p className='text-gray text-label-sm-regular text-center'>
                            This action cannot be undone. All selected customers and their data will be permanently
                            deleted.
                        </p>

                        <label className='flex cursor-pointer justify-center gap-2 rounded-xl p-4'>
                            <StanCheckbox
                                checked={confirmed}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmed(e.target.checked)}
                            />
                            <span className='text-label-sm-regular text-dark pt-1'>
                                I understand that{' '}
                                <span className='text-negative'>
                                    {totalCustomers} {customerText}
                                </span>{' '}
                                will be permanently deleted.
                            </span>
                        </label>
                    </div>
                )}
            </div>
        </StanModal>
    );
}

BulkDeleteCustomersModal.displayName = 'BulkDeleteCustomersModal';
